import React from 'react'
import * as H from 'history'
import { LayoutApi } from 'json-react-layouts'
import { connect } from 'react-redux'

import { ComponentServices } from 'common/routing/page-definition'
import {
    getRegisteredRoutes,
    createRouteServices,
    RouteResolver,
    RouteResolution,
} from 'common/routing/route-resolver'
import { AdState } from 'common/advertising/ad-state'
import { AppState } from 'common/store'
import { TogglesState } from 'common/feature-togglings/reducer'
import { RenderTarget } from 'common/context-providers/render-target-context'
import { RouteInfoResolverFailedEvent, PageError } from 'common/events/page-events'
import { AdDefinition } from 'common/advertising/add-ads'

import { RedirectRenderer } from 'component-library/__App/component-rendering/RedirectRenderer'
import { renderRouteInfo } from 'component-library/routing/render-route-info'

import { getRouteForResolution } from './get-route-info-for-resolution'
import { DataLoaderGlobalParams } from '../data/resources'
import { getSiteSection } from './helpers'
import { AllEvents } from 'common/events/all-events'

export interface Props {
    hostname: string | undefined
    protocol: string | undefined
    location: H.Location
    services: DataLoaderGlobalParams
    layout: LayoutApi<any, any, ComponentServices, any, any>
    /** Ad definitions for ads which are present on all pages (and defined outside route info) */
    siteAds: AdDefinition[]
    renderTarget: RenderTarget

    stretchColumns: boolean
    renderPage: (
        pageContents: React.ReactElement<any>,
        ads: AdState | undefined,
        pageType: string,
        section: string,
        additionalPageProperties: {
            [key: string]: any
        }
    ) => React.ReactElement<any>
    onEvent: (event: AllEvents) => void

    refreshOverride?: () => void
}

export interface State {
    error?: PageError
    prevLocation?: H.Location
}

export const PageResolver = connect((state: AppState) => ({ toggles: state.toggles }))(
    class PageResolver extends React.Component<Props & { toggles: TogglesState }, State> {
        state: State = {}

        static getDerivedStateFromProps(props: Props, state: State): Partial<State> {
            return {
                // If location changed, clear any errors we have
                error: state.prevLocation === props.location ? state.error : undefined,
                prevLocation: props.location,
            }
        }

        static getDerivedStateFromError(err: Error): Partial<State> {
            if (err.message === '404') {
                return { error: { errorType: '404' } }
            }
            const error: PageError = { errorType: 'PageRendererFailed' }
            return { error }
        }

        componentDidCatch(err: Error, info: React.ErrorInfo) {
            const {
                services: { log },
            } = this.props
            // The data loader formats the message, so it looks like this:

            if (err.message === '404') {
                return
            }

            const message = `Failed to render page at location ${this.props.location.pathname}`
            log.error({ err, info }, message)
            const error: PageError = { errorType: 'PageRendererFailed' }
            // raise the event
            this.props.onEvent({
                type: 'page-render.failed',
                originator: 'PageRenderer',
                payload: {
                    error,
                    message,
                },
            })
        }

        handleRouteInfoFailure = (event: RouteInfoResolverFailedEvent) => {
            this.setState({
                error: event.payload.error,
            })
            this.props.onEvent(event)
        }

        render() {
            const props = this.props
            const errorThrown = this.state.error

            const { section } = getSiteSection(
                props.location.pathname,
                props.hostname,
                props.services
            )

            // Handle errors from this error boundary
            if (errorThrown && errorThrown.errorType === '404') {
                props.services.log.info({ section }, `Rendering 404 page`)
                const { notFoundRouteInfo } = getRegisteredRoutes()
                const routeServices = createRouteServices(
                    props.services,
                    {
                        type: 'error',
                        error: { errorType: '404' },
                        section,
                    },
                    props.location,
                    props.hostname,
                    props.protocol,
                    props.renderTarget
                )
                return renderRouteInfo(routeServices, notFoundRouteInfo(routeServices), this.props)
            } else if (errorThrown) {
                props.services.log.info({ section }, `Rendering error page`)
                const { errorRouteInfo } = getRegisteredRoutes()
                const routeServices = createRouteServices(
                    props.services,
                    {
                        type: 'error',
                        error: errorThrown,
                        section,
                    },
                    props.location,
                    props.hostname,
                    props.protocol,
                    props.renderTarget
                )
                return renderRouteInfo(routeServices, errorRouteInfo(routeServices), this.props)
            }

            return (
                <RouteResolver
                    hostname={props.hostname}
                    path={props.location.pathname}
                    search={props.location.search}
                    services={props.services}
                    renderData={renderProps => {
                        const routeResolution: RouteResolution = renderProps.data.hasData
                            ? renderProps.data.result
                            : // TODO Route hints
                              {
                                  type: 'loading',
                                  section,
                              }

                        const routeServices = createRouteServices(
                            props.services,
                            routeResolution,
                            props.location,
                            props.hostname,
                            props.protocol,
                            props.renderTarget
                        )
                        const route = getRouteForResolution(routeServices)

                        if (route && route.kind === 'redirect') {
                            return <RedirectRenderer log={props.services.log} routeInfo={route} />
                        }

                        return renderRouteInfo(routeServices, route, this.props)
                    }}
                />
            )
        }
    }
)
